/**
 * ConceptLabView — Parsed Feedback view for `concept_lab` runs. These runs
 * have no formalized OutputSchemaType (PROMPT_OUTPUT_SCHEMA['concept_lab'] is
 * null by design), so ParsedFeedbackBody would otherwise dump their raw JSON.
 * This view lays the parsed concept fields out as labeled sections with
 * LaTeX-formatted values — matching how solution-analysis / ExtractedStepsView
 * render their extracted fields. Reuses the shared feedback-section classes so
 * it inherits the same styling.
 */
import { LatexText } from '@shared-ui/latex';

interface ProbingQuestionLike {
    id?: string;
    question: string;
    options?: string[];
    correctOptionIndex?: number;
    explanation?: string;
}
interface MistakeTargetedLike {
    source?: string;
    questionId?: string | null;
    questionText: string;
    relevanceReason?: string;
}
interface RelatedConceptLike {
    conceptName: string;
    relationship?: string;
    briefDescription?: string;
}

/** Concept-lab response shape (mirrors ConceptLabLlmResponse). Kept local to
 *  shared-ui so this view takes no host-app type. */
export interface ConceptLabLike {
    conceptSpotlight?: string;
    personalizedNarrative?: string;
    probingQuestions?: ProbingQuestionLike[];
    mistakeTargetedQuestions?: MistakeTargetedLike[];
    relatedConcepts?: RelatedConceptLike[];
}

/** Narrow a parsed JSON value to a concept-lab response, or null if it isn't one. */
export function asConceptLab(value: unknown): ConceptLabLike | null {
    if (!value || typeof value !== 'object') return null;
    const o = value as Record<string, unknown>;
    const looksLikeConcept =
        typeof o.conceptSpotlight === 'string' ||
        typeof o.personalizedNarrative === 'string' ||
        Array.isArray(o.probingQuestions);
    return looksLikeConcept ? (o as ConceptLabLike) : null;
}

export function ConceptLabView({ data }: { data: ConceptLabLike }) {
    return (
        <div className="feedback-content">
            <div className="feedback-sections" style={{ marginBottom: 12 }}>
                <div className="feedback-section">
                    <h4>Concept Spotlight</h4>
                    {data.conceptSpotlight ? <p><LatexText text={data.conceptSpotlight} /></p> : <p className="empty">-</p>}
                </div>
                <div className="feedback-section">
                    <h4>Personalized Narrative</h4>
                    {data.personalizedNarrative ? <p><LatexText text={data.personalizedNarrative} /></p> : <p className="empty">-</p>}
                </div>
            </div>

            {data.probingQuestions?.length ? (
                <div className="feedback-section" style={{ marginBottom: 12 }}>
                    <h4>Probing Questions</h4>
                    <div className="sa-steps-list">
                        {data.probingQuestions.map((q, i) => (
                            <div key={q.id ?? i} className="sa-step-item correct">
                                <div className="sa-step-rail">Q{i + 1}</div>
                                <div className="sa-step-body">
                                    <div className="sa-step-content"><LatexText text={q.question} /></div>
                                    {q.options?.length ? (
                                        <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                                            {q.options.map((opt, oi) => (
                                                <li key={oi} style={oi === q.correctOptionIndex ? { fontWeight: 600 } : undefined}>
                                                    <LatexText text={opt} />{oi === q.correctOptionIndex ? ' ✓' : ''}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : null}
                                    {q.explanation ? <div className="extracted-step-meta"><LatexText text={q.explanation} /></div> : null}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {data.mistakeTargetedQuestions?.length ? (
                <div className="feedback-section" style={{ marginBottom: 12 }}>
                    <h4>Mistake-Targeted Questions</h4>
                    <div className="sa-steps-list">
                        {data.mistakeTargetedQuestions.map((m, i) => (
                            <div key={m.questionId ?? i} className="sa-step-item correct">
                                <div className="sa-step-body">
                                    <div className="sa-step-content"><LatexText text={m.questionText} /></div>
                                    {m.relevanceReason ? <div className="extracted-step-meta"><LatexText text={m.relevanceReason} /></div> : null}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {data.relatedConcepts?.length ? (
                <div className="feedback-section">
                    <h4>Related Concepts</h4>
                    <div className="sa-steps-list">
                        {data.relatedConcepts.map((r, i) => (
                            <div key={i} className="sa-step-item correct">
                                <div className="sa-step-body">
                                    <div className="sa-step-content">
                                        <strong>{r.conceptName}</strong>{r.relationship ? ` — ${r.relationship}` : ''}
                                    </div>
                                    {r.briefDescription ? <div className="extracted-step-meta"><LatexText text={r.briefDescription} /></div> : null}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
